// Áreas e CNAE do contrato (04/10/2026) — tipos e contas da TELA.
//
// Espelha `dre-sistema/lambda/comercial-engine/handlers/contratoAreas.js`.
// Aqui não mora regra de negócio (quem decide o que é pendência, a sugestão e
// o que vai às parcelas é o servidor): só o que a tela precisa para mostrar,
// para montar o pedido e para conferir a soma antes de enviar. Lógica pura,
// testada em `areasContrato.test.ts` (o jest do projeto não carrega antd).
import { AREAS_EMPRESA, rotuloDaArea } from '../../utils/areasEmpresa';

export type Rateio = Record<string, number>;
export type Confianca = 'alta' | 'media' | 'baixa';

export interface SugestaoDivisao {
  rateio: Rateio; origem: string; confianca: Confianca; motivo: string | null; alternativa: Rateio | null;
}
export interface SugestaoCnae {
  codigo: string; origem: string; confianca: Confianca; motivo: string | null; alternativa: string | null;
  por_codigo?: Record<string, number> | null;
}
/** As parcelas que já têm setor/código dizem outra coisa que o contrato. */
export interface Diverge { setor: boolean; cnae: boolean }
export interface NumerosAreas {
  /** Os dois valores em R$ vêm `null` para quem não tem a permissão do financeiro. */
  parcelas: number; valor_parcelas: number | null;
  sem_setor_n: number; sem_setor_valor: number | null;
  abertas_n: number; abertas_sem_cnae_n: number; cnae_divergente_n: number;
  /** 14/10/2026 — para o modo "todas" do modal: já faturadas/recebidas, e com setor diferente da divisão do contrato. */
  faturadas_n?: number; faturadas_valor?: number | null;
  outra_divisao_n?: number; outra_divisao_valor?: number | null;
}
export interface DetalheAreas {
  contrato: { id: string; numero: string; titulo: string; status: string; contrato_pai_id: string | null; pai_numero: string | null; aditivos: number };
  divisao: { rateio: Rateio | null; herdada: boolean; origem: string | null };
  cnae: { codigo: string | null; descricao: string | null; herdado: boolean; manual: boolean };
  por_area: { setor: string; rotulo: string; valor: number | null; proporcao: number }[];
  valores: { total: number; sem_setor: number } | null;
  subareas: { setor: string; rotulo: string; subarea: string | null; valor: number | null }[];
  pendencias: string[]; avisos: string[]; numeros: NumerosAreas;
  diverge?: Diverge;
  sugestao: { divisao: SugestaoDivisao | null; cnae: SugestaoCnae | null };
  pode_editar: boolean;
}
export interface LinhaAreas {
  id: string; numero: string; titulo: string; status: string;
  contrato_pai_id: string | null; pai_numero: string | null;
  cliente_nome: string | null; valor_contratado: string | number | null;
  /** Quantos aditivos este contrato tem (as parcelas dos que o seguem são preenchidas junto). */
  aditivos?: number;
  diverge?: Diverge;
  divisao: Rateio | null; divisao_herdada: boolean; divisao_origem: string | null;
  cnae: string | null; cnae_herdado: boolean; cnae_manual: boolean;
  pendencias: string[]; avisos: string[]; numeros: NumerosAreas;
  sugestao: { divisao: SugestaoDivisao | null; cnae: SugestaoCnae | null };
}
export interface ResumoCarga {
  contratos: number; divisoes: number; cnaes: number;
  parcelas_setor: number; parcelas_setor_valor: number; parcelas_cnae: number;
}
export interface ItemCarga {
  id: string; numero: string; titulo: string; pai_numero: string | null;
  divisao: { rateio: Rateio; origem: string } | null;
  cnae: { codigo: string; origem: string } | null;
  parcelas_setor: { n: number; valor: number; rateio: Rateio } | null;
  parcelas_cnae: { n: number; cnae: string } | null;
}
export interface RespostaPendencias {
  contadores: {
    contratos: number; sem_divisao: number; sem_cnae: number;
    parcelas_sem_setor: { n: number; valor: number; contratos: number };
    parcelas_sem_cnae: { n: number; contratos: number };
    cnae_divergente: { n: number; contratos: number };
  };
  linhas: LinhaAreas[];
  carga: ResumoCarga;
  pode_editar: boolean; pode_carga: boolean;
}

// O que falta, em português de quem usa. `aviso` = só para conferir.
export const PENDENCIAS: Record<string, { rotulo: string; dica: string; cor: string; aviso?: boolean }> = {
  sem_divisao:        { rotulo: 'Sem divisão por área', cor: 'red',
    dica: 'O contrato não diz de quais áreas é. Parcela nova nasce sem setor e a receita fica fora dos setores na DRE.' },
  parcelas_sem_setor: { rotulo: 'Parcela sem setor', cor: 'red',
    dica: 'Há parcela deste contrato sem setor: esse valor aparece como "sem setor" na receita da DRE.' },
  sem_cnae:           { rotulo: 'Sem CNAE', cor: 'orange',
    dica: 'O contrato não tem o código de serviço da nota. Parcela nova nasce sem código.' },
  parcelas_sem_cnae:  { rotulo: 'Parcela em aberto sem CNAE', cor: 'orange',
    dica: 'Há parcela a faturar sem o código de serviço da nota.' },
  cnae_divergente:    { rotulo: 'CNAE diferente do contrato', cor: 'blue', aviso: true,
    dica: 'Parcela em aberto com código diferente do contrato. Pode ser exceção de propósito — só confira.' },
  setor_divergente:   { rotulo: 'Parcelas de outra área', cor: 'blue', aviso: true,
    dica: 'As parcelas que já têm setor dizem outra divisão que a do contrato (ou do contrato principal). O sistema não preenche as vazias sozinho: abra em Editar e decida.' },
};

export const ORIGENS: Record<string, string> = {
  venda: 'serviços vendidos', composicao: 'composição da venda', area_proposta: 'área do negócio',
  parcelas: 'parcelas já lançadas', aditivos: 'parcelas dos aditivos', setor_operacional: 'setor operacional',
  manual: 'definida à mão', area: 'padrão da área', principal: 'contrato principal',
};
export const rotuloOrigem = (o?: string | null) => (o && ORIGENS[o]) || (o ? o : 'início do contrato');

export const CONFIANCA: Record<Confianca, { rotulo: string; cor: string }> = {
  alta:  { rotulo: 'pronta', cor: 'green' },
  media: { rotulo: 'conferir', cor: 'gold' },
  baixa: { rotulo: 'palpite', cor: 'default' },
};

const fmtPct = (p: number) => {
  const v = Math.round(p * 1000) / 10;
  return (Number.isInteger(v) ? String(v) : v.toFixed(1).replace('.', ',')) + '%';
};

// Áreas de um rateio na ordem oficial, com cor e percentual pronto.
export function partesDoRateio(r?: Rateio | null) {
  if (!r) return [];
  return AREAS_EMPRESA.filter(a => (r[a.chave] || 0) > 0)
    .map(a => ({ chave: a.chave, rotulo: a.rotulo, cor: a.cor, proporcao: r[a.chave], pct: fmtPct(r[a.chave]) }));
}
export const descreverRateio = (r?: Rateio | null) =>
  partesDoRateio(r).map(p => (p.proporcao >= 0.9995 ? p.rotulo : `${p.rotulo} ${p.pct}`)).join(' · ');

// ── Formulário de edição: percentuais digitados (texto) ↔ proporção ────────
const lerPct = (v: unknown) => {
  const n = parseFloat(String(v ?? '').trim().replace(/\s|%/g, '').replace(',', '.'));
  return Number.isFinite(n) ? n : 0;
};
export function rateioParaPct(r?: Rateio | null): Record<string, string> {
  const out: Record<string, string> = {};
  for (const a of AREAS_EMPRESA) {
    const p = (r && r[a.chave]) || 0;
    out[a.chave] = p > 0 ? String(Math.round(p * 10000) / 100).replace('.', ',') : '';
  }
  return out;
}
export const somaPct = (pct: Record<string, string>) =>
  Math.round(AREAS_EMPRESA.reduce((s, a) => s + lerPct(pct[a.chave]), 0) * 100) / 100;
// A soma tem que fechar 100% (folga de 0,05 ponto, para 33,33 + 33,33 + 33,34).
export const somaFecha = (pct: Record<string, string>) => Math.abs(somaPct(pct) - 100) <= 0.05;
export const temNegativo = (pct: Record<string, string>) => AREAS_EMPRESA.some(a => lerPct(pct[a.chave]) < 0);

// O que vai ao servidor: proporção 0..1 somando EXATAMENTE 1 (o servidor
// recusa fora de 0,1%). A última área absorve o resto do arredondamento.
export function pctParaRateio(pct: Record<string, string>): Rateio | null {
  const pares = AREAS_EMPRESA.map(a => [a.chave, lerPct(pct[a.chave])] as [string, number]).filter(([, v]) => v > 0);
  if (!pares.length || !somaFecha(pct) || temNegativo(pct)) return null;
  const total = pares.reduce((s, [, v]) => s + v, 0);
  const out: Rateio = {};
  let usado = 0;
  pares.forEach(([k, v], i) => {
    const p = i === pares.length - 1 ? Math.round((1 - usado) * 10000) / 10000 : Math.round((v / total) * 10000) / 10000;
    usado += p;
    if (p > 0) out[k] = p;
  });
  return out;
}

// Dois rateios iguais? (a ordem das chaves não importa — o banco devolve na dele)
export function mesmoRateio(a?: Rateio | null, b?: Rateio | null) {
  if (!a || !b) return !a && !b;
  const chaves = Array.from(new Set([...Object.keys(a), ...Object.keys(b)]));
  return chaves.every(k => Math.abs((a[k] || 0) - (b[k] || 0)) < 0.00005);
}

// Dividir tudo numa área só (atalho do formulário).
export const tudoEm = (chave: string): Record<string, string> =>
  Object.fromEntries(AREAS_EMPRESA.map(a => [a.chave, a.chave === chave ? '100' : '']));

// ── A vista: o que dá para aplicar numa linha com um clique ────────────────
// Devolve o pedido de `op_definir_areas_contrato` para a linha, ou null se não
// há nada que um clique resolva (aí é "Editar"). `soProntas` = só sugestão
// segura (é o critério do "aplicar nas selecionadas" quando a pessoa não
// abriu cada uma).
export function pedidoDaLinha(l: LinhaAreas, opcoes: { soProntas?: boolean } = {}) {
  const serve = (s: { confianca: Confianca } | null) => !!s && (!opcoes.soProntas || s.confianca === 'alta');
  const pedido: { contrato_id: string; rateio?: Rateio; cnae?: string } = { contrato_id: l.id };
  let fazAlgo = false;
  if (!l.divisao && serve(l.sugestao.divisao)) { pedido.rateio = l.sugestao.divisao!.rateio; fazAlgo = true; }
  if (!l.cnae && serve(l.sugestao.cnae)) { pedido.cnae = l.sugestao.cnae!.codigo; fazAlgo = true; }
  // Contrato que JÁ tem divisão/CNAE e só falta levar às parcelas: o pedido
  // sem rateio e sem cnae faz exatamente isso.
  // Quando as parcelas que já têm setor/código dizem OUTRA coisa que o contrato,
  // o servidor não preenche as vazias com um clique — é caso de Editar.
  const divisaoDepois = l.divisao || pedido.rateio;
  const cnaeDepois = l.cnae || pedido.cnae;
  if (divisaoDepois && l.numeros.sem_setor_n > 0 && !l.diverge?.setor) fazAlgo = true;
  if (cnaeDepois && l.numeros.abertas_sem_cnae_n > 0 && !l.diverge?.cnae) fazAlgo = true;
  return fazAlgo ? pedido : null;
}

// O que um clique faria, em uma frase (vai no botão/confirmar).
export function resumoDoPedido(l: LinhaAreas, pedido: { rateio?: Rateio; cnae?: string }) {
  const partes: string[] = [];
  if (pedido.rateio) partes.push(`divisão: ${descreverRateio(pedido.rateio)}`);
  if (pedido.cnae) partes.push(`CNAE ${pedido.cnae}`);
  const divisaoDepois = l.divisao || pedido.rateio;
  const cnaeDepois = l.cnae || pedido.cnae;
  if (divisaoDepois && l.numeros.sem_setor_n > 0 && !l.diverge?.setor) partes.push(`${l.numeros.sem_setor_n} parcela(s) recebem setor`);
  if (cnaeDepois && l.numeros.abertas_sem_cnae_n > 0 && !l.diverge?.cnae) partes.push(`${l.numeros.abertas_sem_cnae_n} parcela(s) em aberto recebem CNAE`);
  // O servidor também completa as parcelas dos aditivos que seguem este contrato.
  if ((l.aditivos || 0) > 0 && (divisaoDepois || cnaeDepois)) partes.push(`vale também para as parcelas dos aditivos que seguem este contrato (${l.aditivos})`);
  return partes.join(' · ');
}

// Ordem de aplicação em lote: PRINCIPAL antes do aditivo. O aditivo sem divisão
// própria segue o principal — aplicado antes, ganharia uma divisão só dele; e o
// servidor (`so_se_vazio`) não fixa nada no aditivo cujo principal acabou de
// ganhar a sua. É a mesma ordem da carga do servidor (`planejarCarga`).
export function ordenarParaLote(linhas: LinhaAreas[]): LinhaAreas[] {
  return [...linhas].sort((a, b) =>
    (a.contrato_pai_id ? 1 : 0) - (b.contrato_pai_id ? 1 : 0) || a.numero.localeCompare(b.numero));
}

// Ordem da lista: primeiro o que mexe na DRE (parcela sem setor, pelo valor),
// depois o que trava nota (parcela em aberto sem CNAE), depois o resto.
export function ordenarLinhas(linhas: LinhaAreas[]): LinhaAreas[] {
  const peso = (l: LinhaAreas) =>
    (l.numeros.sem_setor_n > 0 ? 4 : 0) + (l.numeros.abertas_sem_cnae_n > 0 ? 2 : 0) + (l.pendencias.length ? 1 : 0);
  return [...linhas].sort((a, b) =>
    peso(b) - peso(a) || (b.numeros.sem_setor_valor || 0) - (a.numeros.sem_setor_valor || 0) || b.numero.localeCompare(a.numero));
}

export const areaDominante = (r?: Rateio | null) => {
  const p = partesDoRateio(r).sort((a, b) => b.proporcao - a.proporcao)[0];
  return p ? rotuloDaArea(p.chave) : '';
};
